import React, { useEffect, useMemo, useState } from 'react';
import { updateCardStatus } from '../../../../../Actions/CardsActions';

const Modal = ({ isOpen, title, subtitle, reasons, confirmLabel, onClose, onConfirm }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [result, setResult] = useState(null); // { title, subtitle }
  const [error, setError] = useState('');

  const canConfirm = useMemo(() => Boolean(selectedReason), [selectedReason]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason('');
      setResult(null);
      setError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setSelectedReason('');
    if (typeof onClose === 'function') onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="relative w-full max-w-lg rounded-xl bg-[#F3EDF9] p-6 shadow-xl py-12 px-24">
        <button
          aria-label="Close"
          onClick={handleClose}
          className="absolute right-12 top-12 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-purple-700 hover:bg-purple-50 focus:outline-none"
        >
          <img src="/Assets/Images/PurpleCrossIcon.svg" alt="Close" className="w-4 h-4" />
        </button>

        {result ? (
          <div className="mb-6 mt-2 text-center pt-8">
            <div className="whitespace-pre-line text-black font-extrabold leading-snug text-3xl">
              {result.title}
            </div>
            {result.subtitle && (
              <div className="mt-6 text-base sm:text-lg text-gray-800 max-w-md mx-auto">
                {result.subtitle}
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6 mt-2 text-center">
            <div className="text-2xl font-extrabold text-black leading-snug whitespace-pre-line">{title}</div>
            {subtitle && <div className="mt-2 text-base text-gray-700">{subtitle}</div>}
          </div>
        )}

        {!result && (
          <div>
            {error && (
              <div className="mb-3 rounded bg-red-100 px-3 py-2 text-sm text-red-800 border border-red-200">
                {error}
              </div>
            )}
            <div className="mb-2 text-sm font-semibold text-gray-700">Select Reason</div>
            <div className="space-y-3">
              {reasons.map((r) => {
                const isSelected = selectedReason === r;
                return (
                  <label key={r} className={`flex items-center gap-3 rounded-md border px-3 py-2 bg-[#FFFFFF] ${isSelected ? 'border-purple-600' : 'border-purple-300'}`}>
                    <span className="relative inline-flex h-4 w-4 items-center justify-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setSelectedReason(isSelected ? '' : r)}
                        className={`h-4 w-4 rounded border-2 ${isSelected ? 'border-[#753ABF]' : 'border-purple-400'} bg-transparent appearance-none outline-none focus:outline-none`}
                        aria-checked={isSelected}
                      />
                      {isSelected && (
                        <svg
                          className="pointer-events-none absolute h-3 w-3 text-[#753ABF]"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className="text-sm text-black">{r}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {!result && (
          <div className="mt-8 flex items-center gap-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-md bg-[#753ABF] px-4 py-2 text-white hover:bg-[#753ABF]/90 focus:outline-none"
            >
              No
            </button>
            <button
              type="button"
              disabled={!canConfirm}
              onClick={async () => {
                if (!canConfirm) return;
                try {
                  setError('');
                  const maybe = onConfirm?.(selectedReason);
                  const resultObj = maybe && typeof maybe.then === 'function' ? await maybe : maybe;
                  if (resultObj && (resultObj.title || resultObj.subtitle)) {
                    setResult({ title: resultObj.title, subtitle: resultObj.subtitle });
                  }
                } catch (e) {
                  setError(e?.message || 'Failed to process request');
                } finally {
                  setSelectedReason('');
                }
              }}
              className={`flex-1 rounded-md px-4 py-2 focus:outline-none ${canConfirm ? 'bg-[#E0D2EE] text-[#753ABF] hover:bg-[#E0D2EE]/90' : 'bg-[#CCCCCC] text-[#666666] cursor-not-allowed'}`}
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState('disable'); // 'disable' | 'delete'
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <div className="min-h-full w-full bg-[#F3EDF9] p-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-8" aria-label="Tabs">
          <button
            className={`whitespace-nowrap border-b-2 px-1 pb-2 text-base font-semibold ${
              activeTab === 'disable' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-700 hover:text-purple-700'
            } focus:outline-none`}
            onClick={() => setActiveTab('disable')}
          >
            Disable Card
          </button>
          <button
            className={`whitespace-nowrap border-b-2 px-1 pb-2 text-base font-semibold ${
              activeTab === 'delete' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-700 hover:text-purple-700'
            } focus:outline-none`}
            onClick={() => setActiveTab('delete')}
          >
            Delete Account
          </button>
        </nav>
      </div>

      {activeTab === 'disable' && (
        <div className="mt-6 max-w-3xl">
          <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
            <div className="text-sm font-semibold text-black">Disable Card</div>
            <button
              onClick={() => setShowDisableModal(true)}
              className="rounded-md bg-purple-600 px-5 py-2 text-white hover:bg-purple-700 focus:outline-none"
            >
              Click Here
            </button>
          </div>
        </div>
      )}

      {activeTab === 'delete' && (
        <div className="mt-6 max-w-3xl">
          <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
            <div className="text-sm font-semibold text-black">Delete Account</div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="rounded-md bg-purple-600 px-5 py-2 text-white hover:bg-purple-700 focus:outline-none"
            >
              Click Here
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={showDisableModal}
        title={'Why do you want to\nDisable Card?'}
        reasons={["Lost card", "Damaged card", "Other"]}
        confirmLabel="Disable"
        onClose={() => setShowDisableModal(false)}
        onConfirm={async (reason) => {
          const res = await updateCardStatus('disabled', reason);
          if (res) {
            return {
              title: 'Your card has been\ndisabled.',
              subtitle:
                'New card can be requested by filling form, verification of form information by admin and payment.',
            };
          }
          return null;
        }}
      />

      <Modal
        isOpen={showDeleteModal}
        title={'Do you want to\nDelete card?'}
        subtitle="This is a permanent action"
        reasons={["Not needed", "Lost/Damaged card", "Other"]}
        confirmLabel="Delete"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async (reason) => {
          const res = await updateCardStatus('deleted', reason);
          if (res) {
            return {
              title: 'Your card has been\nDeleted',
              subtitle:
                'New card can be requested by filling form, verification of form information by admin and payment.',
            };
          }
          return null;
        }}
      />
    </div>
  );
};

export default Settings;
